import type React from 'react';
import { useEffect } from 'react';

import { Box, ToggleButton } from '@zillow/constellation-10';
import { Divider, FormField, Label, Textarea } from '@zillow/constellation-10';

import type { SwitchObject } from '../../../models/Types';
import { SMOKING_POLICY } from '../../../constants';
import type { Section } from '../../../models';
import { SmokingPolicySection } from '../../../models';
import SwitchesWithTitle from '../../Widgets/SwitchesWithTitle';
import { isMultiFamily } from '../PropertyInformation/Utilities/MultiFamilyLandlordUtility';
import SmokingAdditionalAreas from './Smoking/SmokingAdditionalAreas';
import SmokingButtonGroup from './Smoking/SmokingButtonGroup';
import SwitchWithTitle from '../../Widgets/SwitchWithTitle';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: SmokingPolicySection) => void;
};

export default function SmokingPolicy_v4({ section, setSection, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    const smokingPolicySection = new SmokingPolicySection(section.initialParams);
    setSection(smokingPolicySection);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  function onRemoveAll(): void {
    section.removeAllIndexes();
    setSection(section);
  }

  function onNoSmokingClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: true,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: false,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
      [SMOKING_POLICY.FIELD_NAME.DESIGNATED_OUTDOORS_SMOKING_AREA_DESCRIPTION]: '',
    });
    onRemoveAll();
  }

  function onCertainAreasClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: false,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: true,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
    });
  }

  function onAllowedThroughoutPropertyClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: false,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: false,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: true,
    });
    onRemoveAll();
  }

  function onInsideUnitToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT);
  }

  function onCommonIndoorAreasToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS);
  }

  function onPorchesPatiosYardsToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS);
  }

  function onOutdoorSmokingToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.DESIGNATED_OUTDOORS_SMOKING_AREA);
  }

  function onOtherUnitToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.OTHER_UNITS);
  }

  function toggleSwitch(key: string): void {
    setSectionValues({
      [key]: !section.getFieldValue(key),
    });
  }

  function onOtherUnitDescriptionChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SMOKING_POLICY.FIELD_NAME.OTHER_UNITS_DESCRIPTION]: value });
  }

  function onDesignatedOutdoorsSmokingAreaDescriptionChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SMOKING_POLICY.FIELD_NAME.DESIGNATED_OUTDOORS_SMOKING_AREA_DESCRIPTION]: value });
  }

  const isNoSmoking = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.NO_SMOKING);
  const isCertainAreas = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS);
  const isAllowedThroughoutProperty = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY);
  const isOutdoorSmokingEnabled = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.DESIGNATED_OUTDOORS_SMOKING_AREA);
  const isOtherUnits = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.OTHER_UNITS);
  const smokingPolicies: Array<SwitchObject> = [
    {
      text: 'Inside the unit',
      onClick: onInsideUnitToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT),
    },
    {
      text: 'Common indoor areas',
      onClick: onCommonIndoorAreasToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS),
    },
    {
      text: 'Porches, patios, and yards adjacent to units',
      onClick: onPorchesPatiosYardsToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS),
    },
  ];
  const isMultiFamilyLandlord = isMultiFamily(section);

  return (
    <>
      <SmokingButtonGroup>
        <ToggleButton data-test="no-smoking" onClick={onNoSmokingClick} selected={isNoSmoking}>
          No, smoking not allowed
        </ToggleButton>
        <ToggleButton data-test="certain-areas" onClick={onCertainAreasClick} selected={isCertainAreas}>
          Yes, allowed in certain areas
        </ToggleButton>
        <ToggleButton
          data-test="throughout-property"
          onClick={onAllowedThroughoutPropertyClick}
          selected={isAllowedThroughoutProperty}
        >
          Yes, allowed throughout property
        </ToggleButton>
      </SmokingButtonGroup>
      {(isCertainAreas || isAllowedThroughoutProperty) && (
        <Box css={{ marginTop: 'layout.tight' }}>
          <SwitchWithTitle
            text="Designated outdoor smoking area"
            onClick={onOutdoorSmokingToggle}
            toggled={isOutdoorSmokingEnabled}
          />
          {isOutdoorSmokingEnabled && (
            <FormField
              required
              css={{ marginTop: 'layout.tighter' }}
              label={<Label>Describe where the outdoor smoking area is</Label>}
              control={
                <Textarea
                  value={section.getFieldValue(SMOKING_POLICY.FIELD_NAME.DESIGNATED_OUTDOORS_SMOKING_AREA_DESCRIPTION)}
                  onChange={onDesignatedOutdoorsSmokingAreaDescriptionChange}
                />
              }
            />
          )}
        </Box>
      )}
      {(isCertainAreas || isAllowedThroughoutProperty) && isMultiFamilyLandlord && (
        <>
          <Box css={{ marginTop: 'layout.tight' }} />
          {(isCertainAreas || isAllowedThroughoutProperty) && (
            <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          )}
          <SwitchWithTitle
            text="Smoking allowed in other units in the building"
            onClick={onOtherUnitToggle}
            toggled={isOtherUnits}
          />
          {isOtherUnits && (
            <FormField
              required
              label={<Label>Describe in which units smoking is allowed</Label>}
              control={
                <Textarea
                  value={section.getFieldValue(SMOKING_POLICY.FIELD_NAME.OTHER_UNITS_DESCRIPTION)}
                  onChange={onOtherUnitDescriptionChange}
                />
              }
            />
          )}
        </>
      )}
      {isCertainAreas && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <SwitchesWithTitle switches={smokingPolicies} legend="Where is smoking allowed?" noBottomMargin />
          <SmokingAdditionalAreas section={section} setSection={setSection} setSectionValues={setSectionValues} />
        </>
      )}
    </>
  );
}

SmokingPolicy_v4.description = 'Asks if smoking is allowed in other units';
